import { PlusOutlined, EditOutlined, DeleteOutlined, ToolOutlined, MedicineBoxOutlined, ProfileOutlined } from "@ant-design/icons";
import { Button, Card, Empty, Form, Input, Modal, Segmented, Select, Space, Table, Tag, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";

const { Title, Text } = Typography;
const PRIMARY = "#242166";

// Disciplines de requalification d'un acte. La valeur (type) est stockée telle quelle sur le Parametre.
const DISCIPLINES = {
    MECANIQUE: { value: "MECANIQUE", label: "Mécanique / Matériel", color: "blue", icon: <ToolOutlined /> },
    MEDICAL: { value: "MEDICAL", label: "Médical", color: "green", icon: <MedicineBoxOutlined /> },
};
const disciplineInfo = (type) => DISCIPLINES[type] || null;

/**
 * V2 du paramétrage des natures d'actes (« /autre-parametres/nature-acte » v1 en React-Bootstrap).
 * Permet de REQUALIFIER chaque acte en « Mécanique / Matériel » ou « Médical » afin que la prise en
 * charge mécanique et la prise en charge médicale ne proposent que les actes de leur discipline.
 * Backend inchangé : entité Parametre (type = discipline, typeParametre = NATURE_ACTE).
 */
const NatureActeV2 = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtre, setFiltre] = useState("TOUS");
    const [recherche, setRecherche] = useState("");

    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [current, setCurrent] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const charger = () => {
        setLoading(true);
        axiosInstance
            .get(`/api/parametre/type/NATURE_ACTE`)
            .then((res) => {
                const sorted = (res.data || []).sort((a, b) =>
                    (a.libelleParametre || "").localeCompare(b.libelleParametre || ""));
                setItems(sorted);
            })
            .catch(() => toast.error("Erreur lors du chargement des natures d'actes."))
            .finally(() => setLoading(false));
    };
    useEffect(() => { charger(); }, []);

    const compteurs = useMemo(() => ({
        TOUS: items.length,
        MECANIQUE: items.filter((i) => i.type === "MECANIQUE").length,
        MEDICAL: items.filter((i) => i.type === "MEDICAL").length,
        NON_CLASSE: items.filter((i) => !i.type).length,
    }), [items]);

    const itemsFiltres = useMemo(() => {
        const q = recherche.trim().toLowerCase();
        return items.filter((i) => {
            const okFiltre = filtre === "TOUS"
                || (filtre === "NON_CLASSE" ? !i.type : i.type === filtre);
            const okQ = !q || (i.libelleParametre || "").toLowerCase().includes(q);
            return okFiltre && okQ;
        });
    }, [items, filtre, recherche]);

    // Requalification RAPIDE (inline) : change la discipline d'un acte sans ouvrir la modale.
    const requalifier = (record, type) => {
        const payload = {
            parametreId: record.parametreId,
            libelleParametre: record.libelleParametre,
            typeParametre: "NATURE_ACTE",
            type,
        };
        axiosInstance.put(`/api/parametre`, payload)
            .then((res) => {
                setItems((prev) => prev.map((i) => (i.parametreId === record.parametreId ? res.data : i)));
                toast.success("Acte requalifié en « " + disciplineInfo(type).label + " ».");
            })
            .catch(() => toast.error("Erreur lors de la requalification."));
    };

    const ouvrirAjout = () => {
        setEditMode(false);
        setCurrent(null);
        form.resetFields();
        setModalOpen(true);
    };
    const ouvrirEdition = (record) => {
        setEditMode(true);
        setCurrent(record);
        form.setFieldsValue({ libelleParametre: record.libelleParametre, type: record.type || undefined });
        setModalOpen(true);
    };

    const enregistrer = async () => {
        try {
            const values = await form.validateFields();
            const libelle = values.libelleParametre.trim();
            const doublon = !editMode && items.some(
                (i) => (i.libelleParametre || "").toLowerCase() === libelle.toLowerCase());
            if (doublon) { toast.error("Cette nature d'acte existe déjà."); return; }

            setSaving(true);
            const payload = {
                parametreId: editMode ? current?.parametreId : undefined,
                libelleParametre: libelle,
                typeParametre: "NATURE_ACTE",
                type: values.type,
            };
            const req = editMode
                ? axiosInstance.put(`/api/parametre`, payload)
                : axiosInstance.post(`/api/parametre`, payload);
            const res = await req;
            setItems((prev) => editMode
                ? prev.map((i) => (i.parametreId === res.data.parametreId ? res.data : i))
                : [res.data, ...prev]);
            toast.success(editMode ? "Nature d'acte modifiée." : "Nature d'acte ajoutée.");
            setModalOpen(false);
        } catch (e) {
            if (!e?.errorFields) toast.error("Erreur lors de l'enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    const supprimer = (record) => {
        Modal.confirm({
            title: "Supprimer cette nature d'acte ?",
            content: <span>« <b>{record.libelleParametre}</b> » — action irréversible.</span>,
            okText: "Supprimer", okType: "danger", cancelText: "Annuler",
            onOk: () => axiosInstance.delete(`/api/parametre/${record.parametreId}`)
                .then(() => {
                    setItems((prev) => prev.filter((i) => i.parametreId !== record.parametreId));
                    toast.success("Nature d'acte supprimée.");
                })
                .catch(() => toast.error("Erreur lors de la suppression.")),
        });
    };

    const columns = [
        {
            title: "Libellé", dataIndex: "libelleParametre", key: "libelle",
            render: (v) => <Text strong style={{ color: PRIMARY }}>{v}</Text>,
        },
        {
            title: "Discipline", dataIndex: "type", key: "type", width: 320,
            render: (type, record) => (
                <Select
                    value={type || undefined}
                    placeholder={<span><Tag color="default">Non classé</Tag></span>}
                    style={{ width: 260 }}
                    onChange={(v) => requalifier(record, v)}
                    options={Object.values(DISCIPLINES).map((d) => ({
                        value: d.value,
                        label: <Space>{d.icon}{d.label}</Space>,
                    }))}
                />
            ),
        },
        {
            title: "Actions", key: "actions", width: 160,
            render: (_, record) => (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => ouvrirEdition(record)}>Modifier</Button>
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => supprimer(record)} />
                </Space>
            ),
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu="PARAMETRES_SINISTRES" />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper" style={{ background: "#f6f7fb" }}>
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#111827" }}>
                                        <ProfileOutlined style={{ marginRight: 8, color: PRIMARY }} />Natures d'actes
                                    </Title>
                                    <Text type="secondary">
                                        Requalifiez chaque acte en « Mécanique / Matériel » ou « Médical » : la prise en
                                        charge mécanique et la prise en charge médicale ne proposent que les actes de leur discipline.
                                    </Text>
                                </div>
                                <Button type="primary" icon={<PlusOutlined />} onClick={ouvrirAjout}>Ajouter un acte</Button>
                            </div>

                            <Card bordered={false} style={{ borderRadius: 12, marginBottom: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: 12 }}>
                                <Space wrap size="middle">
                                    <Segmented
                                        value={filtre}
                                        onChange={setFiltre}
                                        options={[
                                            { label: `Tous (${compteurs.TOUS})`, value: "TOUS" },
                                            { label: `Mécanique / Matériel (${compteurs.MECANIQUE})`, value: "MECANIQUE" },
                                            { label: `Médical (${compteurs.MEDICAL})`, value: "MEDICAL" },
                                            { label: `Non classés (${compteurs.NON_CLASSE})`, value: "NON_CLASSE" },
                                        ]}
                                    />
                                    <Input.Search allowClear placeholder="Rechercher un acte…" style={{ width: 260 }}
                                        value={recherche} onChange={(e) => setRecherche(e.target.value)} />
                                </Space>
                            </Card>

                            {compteurs.NON_CLASSE > 0 && (
                                <div style={{ marginBottom: 12, color: "#b45309", fontSize: 13 }}>
                                    ⚠️ {compteurs.NON_CLASSE} acte(s) non classé(s) : tant qu'un acte n'est pas requalifié,
                                    il reste proposé dans les DEUX types de prise en charge. Classez-le pour le retirer
                                    de la discipline opposée.
                                </div>
                            )}

                            <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: 8 }}>
                                <Table
                                    rowKey="parametreId"
                                    loading={loading}
                                    columns={columns}
                                    dataSource={itemsFiltres}
                                    size="small"
                                    pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (t) => `${t} acte(s)` }}
                                    locale={{ emptyText: <Empty description="Aucune nature d'acte" /> }}
                                />
                            </Card>
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>

            <Modal
                title={editMode ? "Modifier la nature d'acte" : "Ajouter une nature d'acte"}
                open={modalOpen}
                onOk={enregistrer}
                onCancel={() => setModalOpen(false)}
                confirmLoading={saving}
                okText="Enregistrer"
                cancelText="Annuler"
                destroyOnClose
            >
                <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
                    <Form.Item name="libelleParametre" label="Libellé"
                        rules={[{ required: true, message: "Le libellé est obligatoire." }]}>
                        <Input placeholder="Libellé de la nature de l'acte" />
                    </Form.Item>
                    <Form.Item name="type" label="Discipline"
                        rules={[{ required: true, message: "La discipline est obligatoire." }]}>
                        <Select placeholder="Sélectionner la discipline"
                            options={Object.values(DISCIPLINES).map((d) => ({
                                value: d.value, label: <Space>{d.icon}{d.label}</Space>,
                            }))} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default NatureActeV2;
